import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { AppDispatch } from "../../../redux/store/store";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  acceptAddRequest,
  denyAddRequest,
} from "../../../redux/actions/circleAction";
import { TableRow, TableCell, Stack, Avatar, Typography } from "@mui/material";
import { AcceptButton, DenyButton } from "../../contribute-btn/styles";
import Label from "../../label/Label";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import CircleTermsModal from "../terms-modal/CircleTermsModal";
import { colors } from "../../../utility/types/constant";
import { selectCircleError } from "../../../redux/selectors/circleSelector";

interface AddRequestTableRowProps {
  avatarUrl: string;
  circleName: string;
  circleId: string;
  circleLeaderName: string;
  memberCount: number;
  accept?: any;
  deny?: any;
  selected: any;
  isVerified: any;
  status: any;
  requestId?: string;
  handleOpenNotificationModal?: any;
  isNew?: boolean;
  onActionComplete: (
    success: boolean,
    message: string,
    avatarUrl: string
  ) => any;
  onSuccess: (title: string, message: string) => any;
  onError: (title: string, message: string) => any;
  onDenyAddRequest: (circleName: string, circleImage: string) => any;
}

const AddRequestTableRow = (props: AddRequestTableRowProps) => {
  const {
    avatarUrl,
    circleName,
    circleId,
    circleLeaderName,
    memberCount,
    // accept,
    requestId,
    selected,
    isVerified,
    status,
    // handleOpenNotificationModal,
    // isNew,
    onActionComplete,
    onDenyAddRequest,
  } = props;

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const isError = useSelector(selectCircleError);

  const [showTermsModal, setShowTermsModal] = useState(false);

  const [termsAgreed, setTermsAgreed] = useState(false);

  const handleShowTerms = () => {
    setShowTermsModal(true);
  };

  const handleAgreeToTerms = async () => {
    setTermsAgreed(true);
    setShowTermsModal(false);

    try {
      await dispatch(
        acceptAddRequest({
          requestId: requestId ?? "",
          hasAgreedToCircleTerms: true,
        })
      ).unwrap();

      onActionComplete(
        true,
        `Congratulations! You have successfully joined ${circleName}.`,
        avatarUrl
      );

      setTimeout(() => navigate(`/circle/${circleId}`), 10000);
    } catch (error) {
      onActionComplete(
        false,
        isError || "An unexpected error occurred. Please try again later.",
        avatarUrl
      );
    }
  };

  const handleDisagree = () => {
    setTermsAgreed(false);
    setShowTermsModal(false);
    onActionComplete(
      false,
      isError ||
        `You cannot become a member of ${circleName} without agreeing to the terms and conditions.`,
      avatarUrl
    );
  };

  const handleDenyAddRequest = (circleName: string, circleImage: string) => {
    onDenyAddRequest(circleName, circleImage);
  };

  return (
    <>
      <TableRow hover tabIndex={-1} role="checkbox" selected={selected}>
        <TableCell component="th" scope="row" padding="none">
          <Stack sx={{ ml: 2 }} direction="row" alignItems="center" spacing={2}>
            <Avatar alt={circleLeaderName} src={avatarUrl} />
            <Typography variant="subtitle2" noWrap>
              {circleName}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell sx={{ textAlign: "center" }}>{circleLeaderName}</TableCell>
        <TableCell sx={{ textAlign: "center" }}>{memberCount}</TableCell>
        <TableCell align="center">{isVerified ? "Yes" : "No"}</TableCell>
        <TableCell align="center">
          <Label color={(status === "Rejected" && "error") || "success"}>
            {status}
          </Label>
        </TableCell>
        <TableCell sx={{ textAlign: "center" }}>
          <AcceptBtn
            disabled={status === "Approved" || status === "Rejected"}
            onClick={handleShowTerms}
          />
        </TableCell>
        <TableCell sx={{ textAlign: "center" }}>
          <DenyBtn
            disabled={status === "Rejected" || status === "Approved"}
            requestId={requestId ?? ""}
            // handleOpenNotificationModal={handleOpenNotificationModal}
            onClick={handleDenyAddRequest}
          />
        </TableCell>
      </TableRow>

      <CircleTermsModal
        open={showTermsModal}
        handleClose={() => setShowTermsModal(false)}
        onAgree={handleAgreeToTerms}
        onDisAgree={handleDisagree}
        title={`Terms & Conditions of ${circleName}`}
      />
    </>
  );
};

AddRequestTableRow.propTypes = {
  avatarUrl: PropTypes.string,
  circleName: PropTypes.string,
  circleLeaderName: PropTypes.any,
  memberCount: PropTypes.any,
  selected: PropTypes.any,
  accept: PropTypes.any,
  deny: PropTypes.any,
  isVerified: PropTypes.any,
  status: PropTypes.any,
  loading: PropTypes.bool,
  isNew: PropTypes.bool,
  onActionComplete: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

interface AcceptBtnProps {
  onClick: (e: any) => void;
  disabled?: any;
}

function AcceptBtn(props: AcceptBtnProps) {
  const { onClick, disabled } = props;
  const [showLoading, setShowLoading] = useState(false);

  const handleClick = async (e: any) => {
    setShowLoading(true);
    try {
      await onClick(e);
    } finally {
      setShowLoading(false);
    }
  };

  return (
    <Stack
      spacing={2}
      direction="row"
      sx={{
        textAlign: "center",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <AcceptButton
        disabled={disabled}
        onClick={onClick}
        variant="contained"
        size="small"
      >
        {/* {showLoading ? (
          <LoadingSpinner
            loading={showLoading}
            sx={{ color: colors.white }}
            size={30}
            color={colors.white}
          />
        ) : (
          "Accept"
        )} */}
        Accept
      </AcceptButton>
    </Stack>
  );
}

AcceptBtn.propType = {
  onClick: PropTypes.func,
  disabled: PropTypes.any,
};

interface DenyBtnProps {
  requestId: string;
  onClick?: any;
  disabled?: any;
  handleOpenNotificationModal?: any;
}

function DenyBtn(props: DenyBtnProps) {
  const { requestId, onClick, disabled } = props;
  const [showLoading, setShowLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const handleDeny = async () => {
    try {
      setShowLoading(true);
      const response = await dispatch(denyAddRequest(requestId)).unwrap();
      // await dispatch(fetchAddRequest(userData?._id ?? ""));
      if (response) {
        setShowLoading(false);
      }
      if (onClick) {
        onClick();
      }
    } catch (error) {
      setShowLoading(false);
    }
  };

  return (
    <Stack
      spacing={2}
      direction="row"
      sx={{
        textAlign: "center",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <DenyButton
        disabled={disabled}
        onClick={handleDeny}
        variant="contained"
        size="small"
      >
        {showLoading ? (
          <LoadingSpinner
            loading={showLoading}
            sx={{ color: colors.white }}
            size={30}
            color={colors.white}
          />
        ) : (
          "Deny"
        )}
      </DenyButton>
    </Stack>
  );
}

DenyBtn.propType = {
  onClick: PropTypes.func,
  disabled: PropTypes.any,
  handleOpenNotificationModal: PropTypes.any,
};

export default AddRequestTableRow;
